// ─────────────────────────────────────────────────────────────────────────────
// Conjugarden data API — the contract shared by the browser (TanStack Query
// hooks) and the serverless handler. Pure types + pure functions only, so this
// module is safe to import from both client and server bundles.
//
// Social features (profile, leaderboard, friends) require an authenticated
// Auth0 session. Everything else in the app works without an account.
// ─────────────────────────────────────────────────────────────────────────────

export const API_BASE = '/api/data'

// ── Stored shapes ────────────────────────────────────────────────────────────
export type WardenProfile = {
  userId: string
  handle: string
  avatar: string
  bio: string
  friends: string[]        // userIds
  createdAt: number
  updatedAt: number
}

// A compact, per-language snapshot of a Warden's garden, pushed on login/sync.
// This is what the leaderboard ranks — never the full garden.
export type GardenSummary = {
  lang: string
  restoration: number      // 0..100
  specimens: number
  biomesCleared: number
  bestStreak: number
  pollen: number
  wins: number
  totalCorrect: number
  updatedAt: number
}

export type LeaderboardEntry = {
  userId: string
  handle: string
  avatar: string
  title: string
  restoration: number
  specimens: number
  biomesCleared: number
  bestStreak: number
  pollen: number
  wins: number
  totalCorrect: number
  score: number
  lastActive: string
  isYou?: boolean
}

// ── Request / response payloads ──────────────────────────────────────────────
export type MeResponse = {
  authenticated: boolean
  userId?: string
  profile?: WardenProfile
}
export type UpdateProfileBody = { handle?: string; avatar?: string; bio?: string }
export type SyncGardenBody = { summary: GardenSummary }
export type FriendBody = { friendId: string }
export type LeaderboardResponse = { scope: 'global' | 'friends'; lang: string; entries: LeaderboardEntry[] }
export type FriendsResponse = { friends: (LeaderboardEntry)[] }
export type SearchResponse = { wardens: LeaderboardEntry[] }
export type ProfileResponse = { profile: WardenProfile | null; summary: GardenSummary | null; score: number; title: string }

// ── Canonical ranking — must match on client and server ──────────────────────
export function wardenScore(s: { restoration: number; specimens: number; biomesCleared: number; bestStreak: number; wins: number; totalCorrect: number }): number {
  return Math.round(
    s.restoration * 40 + s.specimens * 12 + s.biomesCleared * 60 + s.bestStreak * 5 + s.wins * 30 + s.totalCorrect * 0.5,
  )
}

export function wardenTitle(restoration: number): string {
  if (restoration >= 100) return 'Verdant Sovereign'
  if (restoration >= 85) return 'Hushbane'
  if (restoration >= 65) return 'Verdant Warden'
  if (restoration >= 45) return 'Grovekeeper'
  if (restoration >= 25) return 'Greenhand'
  if (restoration >= 10) return 'Sprout-Warden'
  return 'Ashling'
}
